"use client";

import Link from "next/link";
import { usePathname } from "next/navigation";
import { Fragment, useEffect, useState } from "react";
import { AnimatePresence, motion, useReducedMotion } from "framer-motion";
import { BrandLogo } from "@/components/brand-logo";
import { ThemeToggle } from "@/components/theme-toggle";
import {
  alternateLocaleHref,
  getDictionary,
  localePath,
  pricingPath,
  sectionPagePath,
  type Locale,
} from "@/lib/i18n";
import { blogListPath } from "@/lib/blog-i18n";
import { NavIntegrations, NavIntegrationsMobile } from "@/components/nav-integrations";
import { NavModules, NavModulesMobile } from "@/components/nav-modules";

const easeOut = [0.22, 1, 0.36, 1] as const;

const mobileList = {
  hidden: { opacity: 0 },
  show: {
    opacity: 1,
    transition: { staggerChildren: 0.055, delayChildren: 0.04 },
  },
};

const mobileItem = {
  hidden: { opacity: 0, y: 10 },
  show: {
    opacity: 1,
    y: 0,
    transition: { duration: 0.28, ease: easeOut },
  },
};

export function SiteHeader({ locale }: { locale: Locale }) {
  const pathname = usePathname();
  const d = getDictionary(locale);
  const base = localePath(locale);
  const prefersReducedMotion = useReducedMotion();
  const otherLocaleHref = alternateLocaleHref(pathname, locale);

  const navItems = [
    { href: base, label: d.nav.home },
    { href: sectionPagePath(locale, "about"), label: d.nav.about },
    { href: sectionPagePath(locale, "features"), label: d.nav.features },
    { href: pricingPath(locale), label: d.nav.pricing },
    { href: sectionPagePath(locale, "references"), label: d.nav.references },
    { href: sectionPagePath(locale, "team"), label: d.nav.team },
    { href: blogListPath(locale), label: d.nav.academy },
    { href: sectionPagePath(locale, "contact"), label: d.nav.contact },
  ] as const;

  const [open, setOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 6);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => {
    if (!open) return;
    const onResize = () => {
      if (window.matchMedia("(min-width: 1280px)").matches) setOpen(false);
    };
    window.addEventListener("resize", onResize);
    return () => window.removeEventListener("resize", onResize);
  }, [open]);

  useEffect(() => {
    if (open) document.body.style.overflow = "hidden";
    else document.body.style.overflow = "";
    return () => {
      document.body.style.overflow = "";
    };
  }, [open]);

  const springTap = prefersReducedMotion ? {} : { whileTap: { scale: 0.94 } };

  return (
    <>
      <AnimatePresence>
        {open ? (
          <motion.div
            key="nav-overlay"
            className="fixed inset-0 z-40 bg-foreground/10 backdrop-blur-[3px] xl:hidden"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={prefersReducedMotion ? { duration: 0.15 } : { duration: 0.22, ease: easeOut }}
            onClick={() => setOpen(false)}
            aria-hidden
          />
        ) : null}
      </AnimatePresence>

      <motion.header
        className="sticky top-0 z-50 overflow-visible border-b border-transparent bg-background"
        animate={{
          borderColor: scrolled ? "var(--border)" : "rgba(0,0,0,0)",
          boxShadow: scrolled
            ? prefersReducedMotion
              ? "0 1px 0 0 var(--border)"
              : "0 12px 40px -16px rgba(0,0,0,0.12)"
            : "0 0 0 0 rgba(0,0,0,0)",
        }}
        transition={{ duration: 0.35, ease: easeOut }}
      >
        <div className="mx-auto flex h-18 max-w-7xl items-center gap-4 overflow-visible px-4 sm:h-22 sm:px-6">
          {/* Logo — sola sabit (flex-1) */}
          <div className="flex flex-1 items-center">
            <motion.div
              className="-ml-1 shrink-0 sm:-ml-2"
              whileTap={prefersReducedMotion ? undefined : { scale: 0.98 }}
              transition={{ type: "spring", stiffness: 420, damping: 28 }}
            >
              <Link
                href={base}
                className="inline-flex shrink-0 items-center opacity-90 transition-opacity duration-200 hover:opacity-100"
                onClick={() => setOpen(false)}
                aria-label={d.brand.name}
              >
                <BrandLogo />
              </Link>
            </motion.div>
          </div>

          {/* Menü — ortada (akışta; iki yanda eşit flex-1 → ortalı, çakışmaz) */}
          <nav
            className="hidden shrink-0 flex-nowrap items-center gap-0.5 xl:flex xl:gap-1"
            aria-label={d.a11y.mainNav}
          >
            {navItems.map((item) => {
              // Masaüstünde logo zaten ana sayfaya gidiyor; menü 1280px kapta sığsın diye
              // "Ana sayfa" yalnız mobil menüde kalır.
              if (item.href === base) return null;
              const link = (
                <a
                  key={item.href}
                  href={item.href}
                  className="relative shrink-0 whitespace-nowrap rounded-full px-2.5 py-3 text-[15px] font-light tracking-normal text-[#172554] transition-[letter-spacing,font-weight] duration-300 ease-out hover:tracking-wide hover:font-medium motion-reduce:hover:tracking-normal motion-reduce:hover:font-light dark:text-white cloud:text-[#4338ca] cloud:hover:text-[#6157e1] lg:px-3 2xl:px-2.5 2xl:text-base"
                >
                  {item.label}
                </a>
              );
              if (item.href === sectionPagePath(locale, "features")) {
                return (
                  <Fragment key={item.href}>
                    <NavModules locale={locale} />
                    <NavIntegrations locale={locale} />
                  </Fragment>
                );
              }
              return link;
            })}
          </nav>

          {/* Aksiyonlar — sağa sabit (flex-1) */}
          <div className="flex flex-1 items-center justify-end gap-1 sm:gap-2">
            {locale === "tr" ? (
              <Link
                href={otherLocaleHref}
                className="rounded-full px-3 py-2 text-base font-light text-[#172554]/80 transition-colors duration-200 hover:bg-card hover:text-[#172554] dark:text-white/80 dark:hover:text-white cloud:text-[#4a41c7] cloud:hover:text-[#6157e1] sm:px-4"
                lang="en"
              >
                English
              </Link>
            ) : (
              <Link
                href={otherLocaleHref}
                className="rounded-full px-3 py-2 text-base font-light text-[#172554]/80 transition-colors duration-200 hover:bg-card hover:text-[#172554] dark:text-white/80 dark:hover:text-white cloud:text-[#4a41c7] cloud:hover:text-[#6157e1] sm:px-4"
                lang="tr"
              >
                Türkçe
              </Link>
            )}
            <ThemeToggle />
            <motion.button
              type="button"
              className="inline-flex h-12 w-12 items-center justify-center rounded-full border border-border bg-card text-[#172554] dark:text-white xl:hidden"
              onClick={() => setOpen((o) => !o)}
              aria-expanded={open}
              aria-controls="mobile-nav"
              aria-label={open ? d.a11y.closeMenu : d.a11y.openMenu}
              {...springTap}
              transition={{ type: "spring", stiffness: 500, damping: 32 }}
            >
              <motion.span
                className="flex items-center justify-center"
                animate={{ rotate: open ? 90 : 0 }}
                transition={{ type: "spring", stiffness: 320, damping: 26 }}
              >
                {open ? (
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    width="24"
                    height="24"
                    fill="currentColor"
                    viewBox="0 0 24 24"
                    aria-hidden
                  >
                    <path d="M6.225 4.811a1 1 0 0 0-1.414 1.414L10.586 12 4.81 17.775a1 1 0 1 0 1.414 1.414L12 13.414l5.775 5.775a1 1 0 0 0 1.414-1.414L13.414 12l5.775-5.775a1 1 0 0 0-1.414-1.414L12 10.586 6.225 4.81Z" />
                  </svg>
                ) : (
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    width="24"
                    height="24"
                    fill="currentColor"
                    viewBox="0 0 24 24"
                    aria-hidden
                  >
                    <path d="M4 6h16v2H4V6Zm0 5h16v2H4v-2Zm0 5h16v2H4v-2Z" />
                  </svg>
                )}
              </motion.span>
            </motion.button>
          </div>
        </div>

        <AnimatePresence initial={false}>
          {open ? (
            <motion.div
              id="mobile-nav"
              key="panel"
              role="dialog"
              aria-modal="true"
              className="overflow-hidden border-t border-border bg-background/98 backdrop-blur-md xl:hidden"
              initial={prefersReducedMotion ? false : { height: 0, opacity: 0.6 }}
              animate={{ height: "auto", opacity: 1 }}
              exit={prefersReducedMotion ? undefined : { height: 0, opacity: 0.85 }}
              transition={
                prefersReducedMotion
                  ? { duration: 0.15 }
                  : { height: { duration: 0.38, ease: easeOut }, opacity: { duration: 0.22 } }
              }
            >
              <motion.nav
                className="mx-auto flex max-h-[min(70vh,calc(100dvh-4.5rem))] max-w-5xl flex-col overflow-y-auto px-4 py-4"
                aria-label={d.a11y.mobileNav}
                variants={prefersReducedMotion ? undefined : mobileList}
                initial="hidden"
                animate="show"
              >
                {navItems.map((item) => {
                  const link = (
                    <motion.a
                      key={item.href}
                      href={item.href}
                      variants={prefersReducedMotion ? undefined : mobileItem}
                      className="rounded-xl px-3 py-4 text-lg font-light text-[#172554]/85 transition-colors duration-200 hover:bg-card hover:text-[#172554] active:text-[#172554] dark:text-white/85 dark:hover:text-white dark:active:text-white cloud:text-[#4338ca]"
                      onClick={() => setOpen(false)}
                    >
                      {item.label}
                    </motion.a>
                  );
                  if (item.href === sectionPagePath(locale, "features")) {
                    return (
                      <Fragment key={item.href}>
                        <NavModulesMobile locale={locale} onNavigate={() => setOpen(false)} />
                        <NavIntegrationsMobile locale={locale} onNavigate={() => setOpen(false)} />
                      </Fragment>
                    );
                  }
                  return link;
                })}
              </motion.nav>
            </motion.div>
          ) : null}
        </AnimatePresence>
      </motion.header>
    </>
  );
}
